import type Graphic from "@arcgis/core/Graphic";
import type GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { getMapView } from "./arcgisUtils";
import { LighthouseData } from "./lighthouseConstants";
import { removeLighthouseEditingOverlay, saveLighthouse } from "./lighthouseUtils";
import { NFSStatus, setNewStatusToFeature } from "./nfsUtils";
import { SectorDraft } from "./sectorDraft";
import { applyDraftsToLighthouse } from "./sectorTableEdit";

export interface CommitSectorTableEditOptions {
    /** The lighthouse being edited, as the sector editor loaded it. */
    lighthouse: LighthouseData;
    /** The table's rows, already validated. */
    drafts: SectorDraft[];
    /** The light the sectors belong to. */
    feature: Graphic;
    trueNorth: number;
    setSelectedFeature: ((feature: Graphic) => void) | null | undefined;
    setLighthouseData: (lighthouseData: LighthouseData) => void;
    setIsSavingLighthouse: (isSaving: boolean) => void;
    setLoading: (isLoading: boolean) => void;
}

/**
 * Writes the definition table's rows onto an existing lighthouse.
 *
 * The table is a draft: nothing here runs until the user saves, which is why this both commits
 * the new sectors to the shared lighthouse state and clears the editing graphics the previous
 * sectors were drawn into.
 *
 * The steps mirror saveSectors in SectorEditorContent, minus its gates - a table that validates
 * has already refused adjacent sectors of the same colour and has shown the IALA advice inline,
 * so there is no warning left to confirm. Deliberately a separate path rather than a shared one:
 * table editing is additive, and the editor's save is not disturbed by it.
 */
export const commitSectorTableEdit = async (options: CommitSectorTableEditOptions) => {
    const updated = applyDraftsToLighthouse(options.lighthouse, options.drafts);

    options.feature.attributes.lighthouseData = updated;

    const mapView = getMapView();
    const lighthouseEditingLayer = mapView?.map?.findLayerById("FyrlyktEditing") as GraphicsLayer | null | undefined;
    lighthouseEditingLayer?.removeAll();
    removeLighthouseEditingOverlay();

    setNewStatusToFeature(options.feature, NFSStatus.PROJECTED_EXISTING);

    await saveLighthouse(
        updated,
        options.setSelectedFeature,
        options.feature,
        options.trueNorth,
        options.setIsSavingLighthouse,
        options.setLoading
    );

    // saveLighthouse renumbers the sectors it is given with the dark ones left out, so a dark
    // sector ends up sharing its index with a lit one. The rows are in angle order, so their
    // position is the index they had - and the sector editor, still open, keys and edits by it.
    for (const [index, sector] of updated.sectors.entries()) {
        sector.index = index;
    }

    // Only now: publishing the new sectors re-runs the sector editor's drawSectors effect, and
    // that opens by clearing the editing layer saveLighthouse reads the geometry out of.
    options.setLighthouseData(updated);

    // The sector editor hid these on the way in; saveSectors puts them back and so must this
    for (const layerId of ["Lys", "Sektorflate", "Sektorlinje"]) {
        const layer = mapView?.map?.findLayerById(layerId);
        if (layer) layer.visible = true;
    }

    // The editor reclones so that consumers watching the feature see a new object
    const reclonedFeature = options.feature.clone();
    reclonedFeature.layer = options.feature.layer;
    options.setSelectedFeature?.(reclonedFeature);

    return updated;
};
